import { formatPhoneNumber } from "../../../utils/FormatPhoneNumber";
import { PersonBodyES, PersonBodyRuby } from "../../contacts/types/Person";

export interface CreatePermitContactFormData {
  userId?: string | number | null;
  contactId: string | null;
  contactType?: string;
  isApplicant: boolean;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  mailAddress?: {
    address: string;
    address2?: string;
    city: string;
    state?: string;
    zip: string;
    countryId: string;
  } | null;
  homeAddress?: {
    address: string;
    address2?: string;
    city: string;
    state?: string;
    zip: string;
    countryId: string;
  } | null;
  sameMailAndPhysicalAddress: boolean;
  shouldBeInvited?: boolean;
  licenseNumber?: string;
  companyName?: string;

  // Optional fields (only used for creating contacts) TODO: would love to remove these eventually
  permitId?: string;
  permitsExtensionDefId?: string | number;
  customFieldType?: number;
}

/**
 * Build payload to create new permit contact from the permits API
 * @param data
 * @param includeContactId
 * @param formatPhone
 */

export function buildNewPermitContactPayload(
  data: CreatePermitContactFormData,
  includeContactId: boolean = true,
  formatPhone = true,
) {
  return {
    user_id: data.userId ? Number(data.userId) : null, // TODO: try to make these camelcase
    ...(includeContactId ? { contact_id: data.contactId ?? null } : {}), // TODO: try to make these camelcase (this is necessary for permits API to recognize this as a new contact field)
    contactType: data.contactType,
    isApplicant: data.isApplicant,
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    phone: data.phone ? (formatPhone ? formatPhoneNumber(data.phone) : data.phone) : "",
    address: data.mailAddress?.address ?? "",
    address2: data.mailAddress?.address2 ?? "",
    city: data.mailAddress?.city ?? "",
    state: data.mailAddress?.state ?? "",
    zip: data.mailAddress?.zip ?? "",
    countryId: data.mailAddress?.countryId ?? "US",
    ...(!data.sameMailAndPhysicalAddress
      ? {
          physicalAddress: data.homeAddress?.address ?? "",
          physicalAddress2: data.homeAddress?.address2 ?? "",
          physicalCity: data.homeAddress?.city ?? "",
          physicalState: data.homeAddress?.state ?? "",
          physicalZip: data.homeAddress?.zip ?? "",
          homeCountryId: data.homeAddress?.countryId ?? "US",
        }
      : {}),
    sameMailAndPhysicalAddress: data.sameMailAndPhysicalAddress ?? true,
    shouldBeInvited: data.shouldBeInvited ?? false,
    ...(data.permitId ? { permitId: data.permitId } : {}),
    ...(data.permitsExtensionDefId ? { permitsExtensionDefId: data.permitsExtensionDefId } : {}),
    ...(data.customFieldType ? { customFieldType: data.customFieldType } : {}),
    ...(data.licenseNumber ? { licenseNumber: data.licenseNumber } : {}),
    ...(data.companyName ? { companyName: data.companyName } : {}),
  };
}

/**
 * Build payload to create new permit contact from the permits API
 * @param data
 */
export function buildUpdatePermitContactPayload(data: {
  contactType: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  mailAddress?: {
    address: string;
    address2?: string;
    city: string;
    state?: string;
    zip: string;
    countryId: string;
  } | null;
  homeAddress?: {
    address: string;
    address2?: string;
    city: string;
    state?: string;
    zip: string;
    countryId: string;
  } | null;
  sameMailAndPhysicalAddress: boolean;
  licenseNumber?: string;
  companyName?: string;
}) {
  const payload = {
    contactType: data.contactType,
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    phone: data.phone ? formatPhoneNumber(data.phone) : "",
    address: data.mailAddress?.address ?? "",
    address2: data.mailAddress?.address2 ?? "",
    city: data.mailAddress?.city ?? "",
    state: data.mailAddress?.state ?? "",
    zip: data.mailAddress?.zip ?? "",
    countryId: data.mailAddress?.countryId ?? "US",
    sameMailAndPhysicalAddress: data.sameMailAndPhysicalAddress ?? true,
    ...(!data.sameMailAndPhysicalAddress
      ? {
          physicalAddress: data.homeAddress?.address ?? "",
          physicalAddress2: data.homeAddress?.address2 ?? "",
          physicalCity: data.homeAddress?.city ?? "",
          physicalState: data.homeAddress?.state ?? "",
          physicalZip: data.homeAddress?.zip ?? "",
          homeCountryId: data.homeAddress?.countryId ?? "US",
        }
      : {}),
    ...(data.licenseNumber ? { licenseNumber: data.licenseNumber } : {}),
    ...(data.companyName ? { companyName: data.companyName } : {}),
  };

  // Return payload with undefined/null values removed
  return Object.entries(payload).reduce(
    (payload, [key, val]) => {
      if (val != null) {
        payload[key] = val;
      }
      return payload;
    },
    {} as { [key: string]: any },
  );
}

/**
 * Creates a new permit contact payload from a person Ruby object
 */
export function personRubyToPermitContactFormData(
  data: PersonBodyRuby,
  userId = null,
  contactId = null,
  contactType = "",
  isApplicant = false,
): CreatePermitContactFormData {
  return {
    userId: userId,
    contactId: contactId,
    contactType: contactType,
    isApplicant: isApplicant,
    firstName: data["first-name"] || "",
    lastName: data["last-name"] || "",
    email: data.email,
    phone: data.phone,
    mailAddress: {
      address: data.address ?? "",
      address2: data.address2,
      city: data.city ?? "",
      state: data.state,
      zip: data.zip ?? "",
      countryId: data["country-id"] ?? "",
    },
    homeAddress: {
      address: data["home-address"] ?? "",
      address2: data["home-address2"],
      city: data["home-city"] ?? "",
      state: data["home-state"],
      zip: data["home-zip"] ?? "",
      countryId: data["home-country-id"] ?? "",
    },
    licenseNumber: data["license-number"],
    companyName: data["company-name"],
    sameMailAndPhysicalAddress: data["same-mail-and-physical-address"] ?? true,
  };
}

/**
 * Creates a new permit contact payload from a person ES object from Elastic Search
 */
export function personESToPermitContactFormData(
  data: PersonBodyES,
  contactId = null,
  contactType = "",
  isApplicant = false,
): CreatePermitContactFormData {
  return {
    userId: data.id,
    contactId: contactId,
    contactType: contactType,
    isApplicant: isApplicant,
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    phone: data.phone,
    mailAddress: {
      address: data.address ?? "",
      address2: data.address2,
      city: data.city ?? "",
      state: data.state,
      zip: data.zip ?? "",
      countryId: data.countryId,
    },
    homeAddress: {
      address: data.physical_address ?? "",
      address2: data.physical_address2,
      city: data.physical_city ?? "",
      state: data.physical_state,
      zip: data.physical_zip ?? "",
      countryId: data.physical_country_id,
    },
    licenseNumber: data.licenseNumber,
    companyName: data.companyName,
    sameMailAndPhysicalAddress: data.same_mail_and_physical_address,
  };
}
